import {
  createDisplayAudioMeter,
  createMicrophoneAudioMeter
} from './audio-meter.js';

const AUDIO_TARGET_SELECTORS = [
  '#tutorial-vu-meter',
  '#tutorial-vu-led-compact',
  '#tutorial-vu-led-scale',
  '#tutorial-level-continuous',
  '#tutorial-level-segments',
  '#tutorial-level-dots-tab'
];

const SILENCE_DB = -120;

const errorMessage = (error, source = 'input') => {
  const name = String(error?.name || '');
  const code = String(error?.code || '');
  const message = String(error?.message || '');
  const raw = `${name} ${code} ${message}`;

  if (/AUDIO_CAPTURE_REQUIRES_SECURE_CONTEXT/i.test(raw)) return 'Requer HTTPS ou localhost';
  if (/NotAllowedError|PermissionDeniedError/i.test(raw)) return source === 'display' ? 'Compartilhamento cancelado' : 'Permissão negada';
  if (/NotFoundError|DevicesNotFoundError/i.test(raw)) return 'Entrada de áudio não encontrada';
  if (/NotReadableError|TrackStartError/i.test(raw)) return 'Entrada em uso ou indisponível';
  if (/OverconstrainedError|ConstraintNotSatisfiedError/i.test(raw)) return 'Entrada incompatível com a configuração';
  if (/SecurityError/i.test(raw)) return 'Captura bloqueada pelo navegador';
  if (/AbortError/i.test(raw)) return 'Falha ao iniciar captura';
  if (/DISPLAY_AUDIO_NOT_AVAILABLE/i.test(raw)) return 'A aba/tela escolhida não compartilhou áudio';
  if (/DISPLAY_CAPTURE_NOT_SUPPORTED/i.test(raw)) return 'Captura de aba indisponível';
  if (/WEB_AUDIO_NOT_SUPPORTED/i.test(raw)) return 'Web Audio indisponível';
  if (/MICROPHONE_NOT_SUPPORTED|AUDIO_INPUT_NOT_AVAILABLE/i.test(raw)) return 'Entrada de áudio indisponível';

  return name ? `Falha: ${name}` : 'Falha ao abrir fonte de áudio';
};

const resolveTargets = () => AUDIO_TARGET_SELECTORS
  .map((selector) => document.querySelector(selector))
  .filter((chart) => chart && typeof chart.update === 'function');

const toStereoValues = (payload = {}) => {
  const channels = Array.isArray(payload.channels) && payload.channels.length
    ? payload.channels
    : [{ db: payload.db, peakDb: payload.peakDb }];
  const silence = { db: SILENCE_DB, peakDb: SILENCE_DB };
  const left = channels[0] || silence;
  const right = channels[1] || silence;

  return [left, right].map((channel) => ({
    value: channel.db ?? SILENCE_DB,
    peak: channel.peakDb ?? channel.db ?? SILENCE_DB
  }));
};

/**
 * Fontes ao vivo do tutorial.
 *
 * Microfone/interface e getDisplayMedia alimentam exatamente o mesmo conjunto
 * de instrumentos. Assim qualquer VU do tutorial pode ser testado tanto com uma
 * entrada física quanto com o áudio compartilhado de uma aba/janela.
 *
 * Os exemplos usam channels 2.0. A camada de áudio separa os canais
 * automaticamente e nunca duplica um canal ausente: uma fonte realmente mono
 * deixa o segundo canal em silêncio. O mesmo FastChart aceita 2.1, 4.1, 5.1 e
 * 7.1; nesses casos o consumidor entrega a mesma quantidade de valores ao
 * chart.update([...]).
 */
export const startVuMicrophoneDemo = () => {
  const controls = document.querySelector('#tutorial-vu-live-controls');

  const micStart = document.querySelector('#tutorial-vu-mic-start');
  const micStop = document.querySelector('#tutorial-vu-mic-stop');
  const micStatus = document.querySelector('#tutorial-vu-mic-status');

  const tabStart = document.querySelector('#tutorial-vu-tab-start');
  const tabStop = document.querySelector('#tutorial-vu-tab-stop');
  const tabStatus = document.querySelector('#tutorial-vu-tab-status');

  if (!controls || !micStart || !micStop || !micStatus || !tabStart || !tabStop || !tabStatus) return null;
  if (controls.dataset.audioDemoMounted === 'true') return null;

  controls.dataset.audioDemoMounted = 'true';

  const setMicRunning = (running) => {
    micStart.disabled = running;
    micStop.disabled = !running;
    micStatus.textContent = running ? 'Entrada ativa' : 'Parado';
  };

  const setTabRunning = (running) => {
    tabStart.disabled = running;
    tabStop.disabled = !running;
    tabStatus.textContent = running ? 'Áudio da aba ativo' : 'Parado';
  };

  const updateAllMeters = (payload) => {
    const values = toStereoValues(payload);
    resolveTargets().forEach((chart) => chart.update(values));
  };

  const resetAllMeters = () => {
    const silence = [
      { value: SILENCE_DB, peak: SILENCE_DB },
      { value: SILENCE_DB, peak: SILENCE_DB }
    ];
    resolveTargets().forEach((chart) => chart.update(silence));
  };

  const microphoneMeter = createMicrophoneAudioMeter({
    floor: -60,
    ceiling: 6,
    gainDb: 18,
    channels: 2,
    fps: 30,
    attack: 0.5,
    release: 0.11,
    onLevel: updateAllMeters
  });

  const displayMeter = createDisplayAudioMeter({
    floor: -60,
    ceiling: 6,
    gainDb: 0,
    channels: 2,
    fps: 30,
    attack: 0.54,
    release: 0.12,
    onLevel: updateAllMeters
  });

  const resetIfIdle = () => {
    if (!microphoneMeter.running && !displayMeter.running) resetAllMeters();
  };

  const onMicStart = async () => {
    micStatus.textContent = 'Solicitando acesso...';
    micStart.disabled = true;

    try {
      await microphoneMeter.start();
      setMicRunning(true);
    } catch (error) {
      console.error('[tutorial][audio input]', error);
      setMicRunning(false);
      micStatus.textContent = errorMessage(error, 'input');
      resetIfIdle();
    }
  };

  const onMicStop = async () => {
    await microphoneMeter.stop();
    setMicRunning(false);
    resetIfIdle();
  };

  const onTabStart = async () => {
    tabStatus.textContent = 'Escolha uma aba com áudio...';
    tabStart.disabled = true;

    try {
      await displayMeter.start();
      setTabRunning(true);
    } catch (error) {
      console.error('[tutorial][tab audio]', error);
      setTabRunning(false);
      tabStatus.textContent = errorMessage(error, 'display');
      resetIfIdle();
    }
  };

  const onTabStop = async () => {
    await displayMeter.stop();
    setTabRunning(false);
    resetIfIdle();
  };

  micStart.addEventListener('click', onMicStart);
  micStop.addEventListener('click', onMicStop);
  tabStart.addEventListener('click', onTabStart);
  tabStop.addEventListener('click', onTabStop);

  setMicRunning(false);
  setTabRunning(false);
  requestAnimationFrame(() => resetAllMeters());

  const cleanup = async () => {
    await Promise.all([
      microphoneMeter.stop(),
      displayMeter.stop()
    ]);
    resetAllMeters();
    micStart.removeEventListener('click', onMicStart);
    micStop.removeEventListener('click', onMicStop);
    tabStart.removeEventListener('click', onTabStart);
    tabStop.removeEventListener('click', onTabStop);
    delete controls.dataset.audioDemoMounted;
  };

  return { microphoneMeter, displayMeter, cleanup };
};

export default startVuMicrophoneDemo;
